import React, { useState } from "react";
import {
	TextField,
	withStyles,
	MenuItem,
	Button,
	Typography
} from "@material-ui/core";
import { Row, Col } from "react-flexbox-grid";
import { Formik } from "formik";
import TypingFilter from "../filters/typingFilter";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { Mutation } from "react-apollo";
import * as Yup from "yup";
import { withNotification } from "../notification/context";
import YearSelector from "../filters/year-selector/multiple";
import useTagValueSelector from "../tags-manager/tag-values-selector";

const constantValues = {
	HUNDRED: 100,
	THOUSAND: 1000,
	TEN_THOUSAND: 10000,
	HUNDRED_THOUSAND: 100000
};

const UPLOAD_FILE_JSON = gql`
	mutation uploadInequalitiesFile(
		$data: InfoInequalitiesFileInput!
		$taskName: String!
		$taskType: String!
	) {
		uploadInequalitiesFile(
			data: $data
			taskName: $taskName
			taskType: $taskType
		) {
			data {
				area
				areaName
				indicator
				constant
				stratifier
				returnAddress
				typing
				years
			}
			taskName
			taskType
		}
	}
`;

const styles = (theme) => ({
	textField: {
		marginLeft: theme.spacing.unit,
		marginRight: theme.spacing.unit
	},
	typing: {
		paddingLeft: 16,
		paddingRight: 0
	},
	actions: {
		textAlign: "right",
		marginTop: 12
	},
	button: {
		margin: 6
	},
	textError: {
		color: "#ef9a9a",
		marginLeft: 12
	}
});

const FieldsSchema = Yup.object().shape({
	returnAddress: Yup.string()
		.trim()
		.matches(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i, "Email inválido")
		.required("Requerido"),
	stratifier: Yup.string().required("Requerido"),
	areaName: Yup.string().required("Requerido")
});

/**
 * Formulario para el cálculo de medidas de desigualdades
 * Jhoan López <jhoanlt19@gmail.com>
 * @param {*} param0
 */
const CalculateInequalityForm = ({
	classes,
	indicator,
	handleClose,
	openNotification
}) => {
	const [typing, setTyping] = useState("");
	const [arrPeriods, setPeriods] = useState([]);
	const [errPeriod, setErrPeriod] = useState(false);
	const [errTyping, setErrTyping] = useState(false);
	const [checked, setChecked] = useState([]);
	let [formValues, setFormValues] = useState({});

	let filter = {
		indicator: indicator.id,
		typing,
		years: arrPeriods
			? arrPeriods.map((item) => ({
					upper: parseInt(item.value.split("-")[0]),
					lower: parseInt(
						item.value.split("-")[1]
							? item.value.split("-")[1]
							: item.value.split("-")[0]
					)
			  }))
			: [],
		stratifier: formValues.stratifier ? formValues.stratifier : ""
	};

	const {
		Component: TagValueSelector,
		clean: cleanTagValues
	} = useTagValueSelector(setChecked, filter);

	//Close and reset form
	function handleCloseForm(resetForm = null) {
		setTyping("");
		setPeriods([]);
		resetForm && resetForm();
		handleClose();
		setChecked([]);
		cleanTagValues();
	}

	return (
		<Mutation mutation={UPLOAD_FILE_JSON}>
			{(save) => {
				return (
					<Formik
						enableReinitialize
						initialValues={{
							area: [],
							constant: constantValues[indicator.constant] || "",
							indicator: "",
							returnAddress: "",
							stratifier: "",
							areaName: ""
						}}
						onSubmit={(values, { resetForm }) => {
							if (arrPeriods.length === 0) {
								setErrPeriod(true);
							} else if (!typing) {
								setErrTyping(true);
							} else {
								delete values.constant;
								setErrPeriod(false);
								setErrTyping(false);
								save({
									variables: {
										data: {
											...values,
											constant: indicator.constant || "",
											area: checked,
											typing,
											years: filter.years.map(
												(item) => `${item.upper}-${item.lower}`
											),
											indicator: indicator.id
										},
										taskName: `Generación de desigualdades ${new Date().toLocaleString()}`,
										taskType: "INEQUALITIES_REPORT"
									}
								})
									.then(() => {
										resetForm();
										handleCloseForm();
										cleanTagValues();
										openNotification(
											"Datos cargados exitosamente, la información se enviará al email que ingresaste."
										);
									})
									.catch((err) => {
										console.log("Error : ", err);
									});
							}
						}}
						validationSchema={FieldsSchema}
					>
						{({ values, errors, handleChange, handleSubmit, resetForm }) => {
							const onChange = (e) => {
								const targetEl = e.target;
								const fieldName = targetEl.name;
								setFormValues({
									...formValues,
									[fieldName]: targetEl.value
								});
								return handleChange(e);
							};

							return (
								<form autoComplete="off" onSubmit={() => null}>
									<Row>
										<Col xs={12} md={6}>
											<TextField
												name="areaName"
												label="Nombre área geográfica*"
												className={classes.textField}
												value={values.areaName}
												onChange={handleChange}
												margin="normal"
												variant="outlined"
												fullWidth
												InputLabelProps={{
													shrink: true
												}}
											></TextField>
											{errors.areaName && (
												<Typography
													variant="caption"
													className={classes.textError}
												>
													{errors.areaName}
												</Typography>
											)}
										</Col>
										<Col xs={12} md={6}>
											<TextField
												name="indicator"
												label="Indicador*"
												className={classes.textField}
												value={indicator ? indicator.name : ""}
												onChange={handleChange}
												margin="normal"
												variant="outlined"
												fullWidth
												InputLabelProps={{
													shrink: true
												}}
												disabled={true}
												placeholder="No se ha seleccionado un indicador"
											></TextField>
										</Col>
									</Row>
									<Row>
										<Col xs={12} md={6}>
											<TextField
												name="constant"
												label="Constante del indicador*"
												className={classes.textField}
												value={values.constant}
												onChange={handleChange}
												margin="normal"
												variant="outlined"
												fullWidth
												InputLabelProps={{
													shrink: true
												}}
												disabled={true}
											></TextField>
										</Col>
										<Col xs={12} md={6}>
											<TextField
												select
												name="stratifier"
												label="Estratificador*"
												className={classes.textField}
												value={values.stratifier}
												onChange={onChange}
												margin="normal"
												variant="outlined"
												fullWidth
												InputLabelProps={{
													shrink: true
												}}
												disabled={
													!indicator ||
													!indicator.stratifiers ||
													indicator.stratifiers.length === 0
												}
											>
												{indicator &&
													indicator.stratifiers &&
													indicator.stratifiers.map((item, key) => (
														<MenuItem key={key} value={item.id}>
															{item.name}
														</MenuItem>
													))}
											</TextField>
											{errors.stratifier && (
												<Typography
													variant="caption"
													className={classes.textError}
												>
													{errors.stratifier}
												</Typography>
											)}
										</Col>
									</Row>
									<Row>
										<Col xs={12} md={6} className={classes.typing}>
											<TypingFilter
												value={typing}
												handleChange={(n, v) => setTyping(v)}
												placeholder="Tipificación*"
												indicator={indicator.id}
											/>
											{errTyping && (
												<Typography
													variant="caption"
													className={classes.textError}
												>
													Requerido
												</Typography>
											)}
										</Col>
										<Col xs={12} md={6}>
											<TextField
												required
												name="returnAddress"
												id="outlined-name"
												label="Email*"
												className={classes.textField}
												value={values.returnAddress}
												onChange={handleChange}
												margin="normal"
												variant="outlined"
												fullWidth
												InputLabelProps={{
													shrink: true
												}}
											/>
											{errors.returnAddress && (
												<Typography
													variant="caption"
													className={classes.textError}
												>
													{errors.returnAddress}
												</Typography>
											)}
										</Col>
									</Row>
									<Row>
										<Col xs={12} md={6}>
											<YearSelector
												label="Período*"
												value={arrPeriods}
												onChange={setPeriods}
												typing={typing}
												indicator={indicator.id}
											/>
											{errPeriod && (
												<Typography
													variant="caption"
													className={classes.textError}
												>
													Requerido
												</Typography>
											)}
										</Col>
									</Row>
									<Row style={{ marginTop: 16 }}>
										<Col xs={12} md={12}>
											<TagValueSelector />
										</Col>
									</Row>
									<div className={classes.actions}>
										<Button
											onClick={handleCloseForm.bind(null, resetForm)}
											color="default"
											variant="outlined"
											className={classes.button}
										>
											Cerrar
										</Button>
										<Button
											onClick={handleSubmit}
											color="primary"
											variant="outlined"
											className={classes.button}
											disabled={checked.length === 0}
										>
											Guardar
										</Button>
									</div>
								</form>
							);
						}}
					</Formik>
				);
			}}
		</Mutation>
	);
};

CalculateInequalityForm.propTypes = {
	classes: PropTypes.object,
	indicator: PropTypes.object,
	availablePeriods: PropTypes.array
};

export default withNotification(withStyles(styles)(CalculateInequalityForm));



// WEBPACK FOOTER //
// ./src/components/tabs/calculateInequalityForm.js